import { useEffect, useRef, useState } from "react";
import Button, { ButtonProps } from "./Button";
import classes from "../../styles/Buttons.module.css";
import { getClasses } from "../../utils";
import { useAnimations, useAppSelector } from "../../hooks";
import { selectButton } from "../../store/slices/buttons";
import Commander from "../../commander";
import Arc from "../Arc";

const SpinButton: React.FC<ButtonProps> = ({ className }) => {
  const button = useRef<HTMLDivElement>(null);
  const { modals, autoplay } = useAppSelector((state) => state);
  // TODO equality function hidre and in every "useAppSelector"
  const buttonData: any = useAppSelector(selectButton.bind(null, "spin"));
  const { available, disabled, hidden, locked } = buttonData;
  const [buttonType, setButtonType] = useState(buttonData.type);
  const [spaceDown, setSpaceDown] = useState(false);
  const [animationClass, playAnimation] = useAnimations(
    {
      spinToStop: {
        duration: 0.25,
        main: classes["spin-stop"],
        atEnd: () => {
          setButtonType("stop");
        },
      },
      stopToSpin: {
        duration: 0.25,
        main: classes["stop-spin"],
        atEnd: () => {
          setButtonType("spin");
        },
      },
    },
    "spin-transitions"
  );

  const isLocked = locked.progress > 0 && locked.progress < 1;
  const onClick = () => {
    if (isLocked && !autoplay.active) return;

    Commander.playSound("button-spin");
    if (autoplay.active) {
      Commander.stopAutoplay();
    } else if (buttonType === "stop") {
      Commander.stopSpin();
    } else {
      Commander.startSpin();
    }
  };
  const mouseEventCfg = { view: window, bubbles: true, cancelable: true };
  useEffect(() => {
    const handelerDown = (e: KeyboardEvent) => {
      if (
        e.code === "Space" &&
        button.current &&
        !modals.backdrop &&
        !spaceDown
      ) {
        const event = new MouseEvent("mousedown", mouseEventCfg);
        setSpaceDown(true);
        button.current.dispatchEvent(event);
      }
    };

    const handelerUp = (e: KeyboardEvent) => {
      if (e.code === "Space" && button.current && !modals.backdrop) {
        setSpaceDown(false);
        const event = new MouseEvent("mouseup", mouseEventCfg);
        button.current.dispatchEvent(event);
      }
    };

    window.addEventListener("keydown", handelerDown);
    window.addEventListener("keyup", handelerUp);

    return () => {
      window.removeEventListener("keydown", handelerDown);
      window.removeEventListener("keyup", handelerUp);
    };
  }, [spaceDown, modals.backdrop]);

  useEffect(() => {
    const transitionTo = (next: "spin" | "stop") => {
      const current = buttonType;
      const transition = current + "-" + next;
      if (current === next) return; // no transiion stop-stop

      if (transition === "spin-stop") {
        playAnimation("spinToStop");
      } else {
        playAnimation("stopToSpin");
      }
    };

    transitionTo(buttonData.type);
    // eslint-disable-next-line
  }, [buttonData.type, buttonType]);

  const typeClass = buttonType === "spin" ? "type-spin" : "type-stop";
  const arcAngle = isLocked && !hidden ? locked.progress * 360 : 0;

  return (
    <div>
      <div className={getClasses(classes, "spin-blocker")}>
        <Arc outerRadius={80} innerRadius={75} angle={arcAngle} />
      </div>
      <Button
        ref={button}
        name="spin"
        className={getClasses(
          classes,
          "spin",
          className,
          typeClass,
          animationClass
        )}
        interactionType="release"
        disabled={disabled}
        onClick={onClick}
        available={available}
        hidden={hidden}
        soundEffect="button-spin"
      >
        <div className={getClasses(classes, "arrows")}>
          <div className={getClasses(classes, "arrow-up")}></div>
          <div className={getClasses(classes, "arrow-down")}></div>
        </div>
        <div className={getClasses(classes, "stop")}>
          <div className={classes["square"]}></div>

          <div className={getClasses(classes, "number", "color text-dark")}>
            {autoplay.active && autoplay.value ? autoplay.value : ""}
          </div>
        </div>
      </Button>
    </div>
  );
};

export default SpinButton;
